import { createStore } from './createStore';

const initialState = {
  scenes: [],
  currentSceneIndex: 0,
  storyboardSceneId: null,
  storyboardPlan: null,
  newlyCompletedSceneIds: new Set(), // Scenes that just finished video generation
  newlyCompletedKeyframeIds: new Set(), // Keyframes that just finished generating (create or edit)
  scenesWithNewKeyframes: new Set(), // Scenes that have keyframes with new updates
};

/**
 * @deprecated SCENES ARE DEPRECATED - Use useVideosStore instead!
 * 
 * This store is being phased out in favor of useVideosStore for the video-centric
 * timeline architecture. The Video model is now the primary entity for timeline
 * operations. Scene data remains ONLY for backward compatibility with legacy code.
 * 
 * Migration guide:
 * - For timeline operations: use useVideosStore
 * - For video ordering: use useVideosStore.getNextOrderIndex()
 * - For video status: use video.status directly, not scene.status
 * - For keyframes: use useProjectKeyframesStore
 * 
 * DO NOT add new scene-based logic. All new code should use useVideosStore.
 */
export const useScenesStore = createStore('ScenesStore', (set, get) => ({
  ...initialState,

  /**
   * Replace all scenes at once.
   * @param {Array<object>} scenes
   */
  setScenes: (scenes) =>
    set({
      scenes: Array.isArray(scenes)
        ? [...scenes].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        : [],
    }),

  /**
   * Add a new scene or update if it already exists.
   * @param {object} scene
   */
  upsertScene: (scene) =>
    set((state) => {
      if (!scene?.id) return state;
      const exists = state.scenes.some((s) => s.id === scene.id);
      let scenes;
      if (exists) {
        scenes = state.scenes.map((s) => (s.id === scene.id ? { ...s, ...scene } : s));
      } else {
        // New scene: if no explicit order, place at the end
        const maxOrder = state.scenes.reduce((max, s) => Math.max(max, s.order ?? 0), -1);
        const sceneWithOrder = (scene.order === undefined || scene.order === null)
          ? { ...scene, order: maxOrder + 1 }
          : scene;
        scenes = [...state.scenes, sceneWithOrder];
      }
      return {
        scenes: scenes.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
      };
    }),

  /**
   * Apply partial updates to a scene.
   * @param {string} sceneId
   * @param {object|Function} updates
   */
  updateScene: (sceneId, updates) =>
    set((state) => ({
      scenes: state.scenes.map((scene) => {
        if (scene.id !== sceneId) return scene;
        const resolvedUpdates =
          typeof updates === 'function' ? updates(scene) : updates;
        return resolvedUpdates ? { ...scene, ...resolvedUpdates } : scene;
      }),
    })),

  /**
   * Remove a scene from the collection.
   * @param {string} sceneId
   */
  deleteScene: (sceneId) =>
    set((state) => ({
      scenes: state.scenes.filter((scene) => scene.id !== sceneId),
      currentSceneIndex:
        state.currentSceneIndex >= state.scenes.length - 1
          ? Math.max(0, state.currentSceneIndex - 1)
          : state.currentSceneIndex,
    })),

  /**
   * Reorder scenes based on an ordered array of scene IDs.
   * @param {string[]} sceneIds - Ordered array of scene IDs
   */
  reorderScenes: (sceneIds) =>
    set((state) => {
      const sceneMap = new Map(state.scenes.map((s) => [s.id, s]));
      const reorderedScenes = sceneIds
        .map((id, index) => {
          const scene = sceneMap.get(id);
          return scene ? { ...scene, order: index } : null;
        })
        .filter(Boolean);
      return { scenes: reorderedScenes };
    }),

  /**
   * Update the currently focused scene index.
   * @param {number} index
   */
  setCurrentSceneIndex: (index) =>
    set((state) => {
      const clamped = Math.min(Math.max(index, 0), Math.max(state.scenes.length - 1, 0));
      return { currentSceneIndex: clamped };
    }),

  /**
   * Remember storyboard context for storyboard-first workflows.
   * @param {string|null} sceneId
   * @param {object|null} plan
   */
  setStoryboardContext: (sceneId, plan) =>
    set({
      storyboardSceneId: sceneId,
      storyboardPlan: plan ?? null,
    }),

  /**
   * Clear all scenes.
   */
  resetScenes: () => set(() => initialState),

  /**
   * Mark a scene as newly completed (just finished video generation).
   * @param {string} sceneId
   */
  markSceneAsNewlyCompleted: (sceneId) =>
    set((state) => ({
      newlyCompletedSceneIds: new Set([...state.newlyCompletedSceneIds, sceneId]),
    })),

  /**
   * Clear the "newly completed" status for a scene (e.g., when user views it).
   * @param {string} sceneId
   */
  clearSceneNewlyCompleted: (sceneId) =>
    set((state) => {
      const updated = new Set(state.newlyCompletedSceneIds);
      updated.delete(sceneId);
      return { newlyCompletedSceneIds: updated };
    }),

  /**
   * Mark a keyframe as newly completed (just finished generating via create or edit).
   * Also marks the parent scene as having new keyframe updates.
   * @param {string} keyframeId
   * @param {string} sceneId - The scene containing this keyframe
   */
  markKeyframeAsNewlyCompleted: (keyframeId, sceneId) =>
    set((state) => ({
      newlyCompletedKeyframeIds: new Set([...state.newlyCompletedKeyframeIds, keyframeId]),
      scenesWithNewKeyframes: new Set([...state.scenesWithNewKeyframes, sceneId]),
    })),

  /**
   * Clear the "newly completed" status for a keyframe (e.g., when user views/approves it).
   * @param {string} keyframeId
   * @param {string} sceneId - The scene containing this keyframe
   */
  clearKeyframeNewlyCompleted: (keyframeId, sceneId) =>
    set((state) => {
      const updatedKeyframes = new Set(state.newlyCompletedKeyframeIds);
      updatedKeyframes.delete(keyframeId);
      
      // Check if this scene still has any newly completed keyframes
      const scene = state.scenes.find(s => s.id === sceneId);
      const sceneStillHasNew = scene?.keyframes?.some(kf => 
        updatedKeyframes.has(kf.id)
      ) ?? false;
      
      const updatedScenes = new Set(state.scenesWithNewKeyframes);
      if (!sceneStillHasNew) {
        updatedScenes.delete(sceneId);
      }
      
      return { 
        newlyCompletedKeyframeIds: updatedKeyframes,
        scenesWithNewKeyframes: updatedScenes,
      };
    }),

  /**
   * Clear the "new keyframes" status for a scene and all its keyframes.
   * @param {string} sceneId
   */
  clearSceneNewKeyframes: (sceneId) =>
    set((state) => {
      const scene = state.scenes.find(s => s.id === sceneId);
      const updatedKeyframes = new Set(state.newlyCompletedKeyframeIds);
      
      // Remove all keyframes from this scene
      scene?.keyframes?.forEach(kf => {
        updatedKeyframes.delete(kf.id);
      });
      
      const updatedScenes = new Set(state.scenesWithNewKeyframes);
      updatedScenes.delete(sceneId);
      
      return {
        newlyCompletedKeyframeIds: updatedKeyframes,
        scenesWithNewKeyframes: updatedScenes,
      };
    }),

  /**
   * Derived helpers
   */
  getCurrentScene: () => get().scenes[get().currentSceneIndex] ?? null,
  getSceneById: (sceneId) => get().scenes.find((scene) => scene.id === sceneId) ?? null,
}));


